import React, { useMemo } from 'react';
import { Base } from '../..';
import type { AuthImageProps } from './types';

/**
 * 处理URL，为网络图片添加token参数
 * @param url 原始URL
 * @param token token值
 * @returns 处理后的URL
 */
function addTokenToUrl(url: string | undefined, token: string | null): string | undefined {
  if (url == null || url === '') {
    return url;
  }

  // 如果没有token，直接返回原始URL
  if (token == null || token === '') {
    return url;
  }

  // 判断是否为网络图片：http:// 或 https:// 或 // 开头
  const isNetworkUrl = url.startsWith('http://')
    || url.startsWith('https://')
    || url.startsWith('//');

  // 不是网络图片，直接返回
  if (!isNetworkUrl) {
    return url;
  }

  // 判断URL是否已有查询参数
  const separator = url.includes('?') ? '&' : '?';

  // 追加token参数
  return `${url}${separator}x-access-token=${token}`;
}

/**
 * 带认证token的图片组件
 * 自动为网络图片（http/https开头）添加token参数
 * 本地图片和相对路径不处理
 *
 * @example
 * ```tsx
 * // 网络图片，自动从localStorage获取token并添加
 * <AuthImage src="https://example.com/img.jpg" />
 * // 输出: https://example.com/img.jpg?x-access-token=abc123
 *
 * // 已有参数的URL
 * <AuthImage src="https://example.com/img.jpg?size=large" />
 * // 输出: https://example.com/img.jpg?size=large&x-access-token=abc123
 *
 * // 本地路径，不处理
 * <AuthImage src="/static/img.jpg" />
 * // 输出: /static/img.jpg
 *
 * // 自定义token
 * <AuthImage src="https://example.com/img.jpg" token="custom-token" />
 * // 输出: https://example.com/img.jpg?x-access-token=custom-token
 *
 * // 禁用token
 * <AuthImage src="https://example.com/img.jpg" autoAddToken={false} />
 * // 输出: https://example.com/img.jpg
 * ```
 */
export function AuthImage(props: AuthImageProps): React.ReactElement {
  const {
    token,
    autoAddToken = true,
    src,
    internal,
    ...restProps
  } = props;

  // 获取token：优先使用props传入的token，否则从localStorage获取
  const accessToken = useMemo(() => {
    if (token != null) {
      return token;
    }
    // 从localStorage获取token
    if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
      return localStorage.getItem('local-access-token');
    }
    return null;
  }, [token]);

  // 处理主图src
  const processedSrc = useMemo(() => {
    // 内网图片不需要添加token
    if (internal === true) {
      return src;
    }

    // 如果禁用自动添加token，直接返回原始src
    if (!autoAddToken) {
      return src;
    }

    return addTokenToUrl(src, accessToken);
  }, [src, accessToken, autoAddToken, internal]);

  return (
    <Base.Image
      {...restProps}
      src={processedSrc}
    />
  );
}

